import { CrmLeadsFilters } from "../../../components/Filters/types";
import { format } from "date-fns";
import { CRM_LEAD_DETAILS_RIGHT_PANEL_SIDEBAR_ITEMS } from "./constants";
import {
  getDefaultCurrencyUnit,
  getDefaultBudgetDuration,
  getLeaseDurationUnit,
} from "../../../utils/constants/mappings";
import { isEmptyPhoneNumber } from "../../../components/PhoneNumberCountryCodeSelector/utils/phoneNumberUtils";
import { LeadDetails } from "../../Communications/utils/types";
import { ExtendedLeadFormValues } from "../../Communications/components/CommunicationDetailsPanel/utils/types";
import { VfLeadFormValues } from "../../../components/Header/components/QuickActionsPopup/VfCreateLeadForm";
import { getDateRangeFromPreset } from "../components/KanbanGrid/utlis/leadTableColumns";

export const formatEditLeadDetailsPayload = (
  values: ExtendedLeadFormValues,
  prevLeadDetails?: LeadDetails | null,
  region?: string
) => {
  const payload: Record<string, unknown> = {};

  if ((values.name || "") !== (prevLeadDetails?.name || "")) {
    payload.name = values.name || null;
  }
  if ((values.email || "") !== (prevLeadDetails?.email || "")) {
    payload.email = values.email || null;
  }
  const currPhoneEmpty = isEmptyPhoneNumber(values?.phoneNumber);
  const prevPhoneEmpty = isEmptyPhoneNumber(prevLeadDetails?.phone);
  const phoneChanged = currPhoneEmpty !== prevPhoneEmpty ||
    (!currPhoneEmpty && !prevPhoneEmpty && (
      values?.phoneNumber?.trim() !== prevLeadDetails?.phone?.trim() ||
      values?.phoneCountry?.dialCode !== prevLeadDetails?.phone_country_code
    ));
  if (phoneChanged) {
    if (currPhoneEmpty) {
      payload.phone = null;
      payload.phone_country_code = null;
    } else if (values?.phoneNumber && values?.phoneCountry?.dialCode) {
      payload.phone = values.phoneNumber;
      payload.phone_country_code = values?.phoneCountry?.dialCode;
    }
  }
  const currWhatsappEmpty = isEmptyPhoneNumber(values?.whatsappNumber);
  const prevWhatsappEmpty = isEmptyPhoneNumber(prevLeadDetails?.whatsapp_phone);
  const whatsappChanged = currWhatsappEmpty !== prevWhatsappEmpty ||
    (!currWhatsappEmpty && !prevWhatsappEmpty && (
      values?.whatsappNumber?.trim() !== prevLeadDetails?.whatsapp_phone?.trim() ||
      values?.whatsappCountry?.dialCode !== prevLeadDetails?.whatsapp_phone_country_code
    ));
  if (whatsappChanged) {
    if (currWhatsappEmpty) {
      payload.whatsapp_phone = null;
      payload.whatsapp_phone_country_code = null;
    } else if (values?.whatsappNumber && values?.whatsappCountry?.dialCode) {
      payload.whatsapp_phone = values.whatsappNumber;
      payload.whatsapp_phone_country_code = values?.whatsappCountry?.dialCode;
    }
  }

  if (
    (values?.destinationCountry || "") !==
    (prevLeadDetails?.demographics?.destination_country_id?.toString() || "")
  ) {
    payload.demographics = {
      ...((payload.demographics as Record<string, unknown>) || {}),
      destination_country_id: values?.destinationCountry
        ? parseInt(values.destinationCountry)
        : null,
      destination_country: values?.destinationCountry
        ? values.destinationCountryName
        : null,
    };
  }
  if (
    (values?.destinationCity || "") !==
    (prevLeadDetails?.demographics?.destination_city_id?.toString() || "")
  ) {
    payload.demographics = {
      ...((payload.demographics as Record<string, unknown>) || {}),
      destination_city_id: values?.destinationCity
        ? parseInt(values.destinationCity)
        : null,
      destination_city: values?.destinationCity
        ? values.destinationCityName
        : null,
    };
  }
  if (
    (values?.university || "") !==
    (prevLeadDetails?.demographics?.university_id?.toString() || "")
  ) {
    payload.demographics = {
      ...((payload.demographics as Record<string, unknown>) || {}),
      university_id: values.university ? parseInt(values.university) : null,
      university: values.universityName || null,
    };
  }
  const currValue = values.move_in_date
    ? format(new Date(values.move_in_date), "yyyy-MM-dd")
    : null;
  const prevValue = prevLeadDetails?.move_in_date
    ? format(new Date(prevLeadDetails.move_in_date), "yyyy-MM-dd")
    : null;
  if (currValue !== prevValue) {
    payload.move_in_date = currValue;
  }

  if (
    JSON.stringify(values?.roomType || []) !==
    JSON.stringify(prevLeadDetails?.room_type || [])
  ) {
    payload.room_type = values?.roomType?.length > 0 ? values.roomType : null;
  }

  if ((values.tenantType || "") !== (prevLeadDetails?.tenant_type || "")) {
    payload.tenant_type = values.tenantType || null;

    if (values.tenantType === "working professional") {
      payload.level_of_study = null;
    }
  }

  if ((values.levelOfStudy || "") !== (prevLeadDetails?.level_of_study || "")) {
    payload.level_of_study = values.levelOfStudy || null;
  }

  if (
    (values.min_budget || "") !==
    (prevLeadDetails?.budget?.min_budget?.toString() || "")
  ) {
    payload.budget = {
      ...((payload.budget as Record<string, unknown>) || {}),
      min_budget: values.min_budget ? Number(values.min_budget) : null,
      max_budget: values.max_budget ? Number(values.max_budget) : null,
      ...(getDefaultCurrencyUnit(region) && {
        currency: getDefaultCurrencyUnit(region),
      }),
      ...(getDefaultBudgetDuration(region) && {
        duration: getDefaultBudgetDuration(region),
      }),
    };
  }

  if (
    (values.max_budget || "") !==
    (prevLeadDetails?.budget?.max_budget?.toString() || "")
  ) {
    payload.budget = {
      ...((payload.budget as Record<string, unknown>) || {}),
      min_budget: values.min_budget ? Number(values.min_budget) : null,
      max_budget: values.max_budget ? Number(values.max_budget) : null,
      ...(getDefaultCurrencyUnit(region) && {
        currency: getDefaultCurrencyUnit(region),
      }),
      ...(getDefaultBudgetDuration(region) && {
        duration: getDefaultBudgetDuration(region),
      }),
    };
  }

  if (
    (values.stay_duration || "") !==
    (prevLeadDetails?.lease?.value?.toString() || "")
  ) {
    payload.lease = {
      ...((payload.lease as Record<string, unknown>) || {}),
      value: values.stay_duration ? Number(values.stay_duration) : null,
      unit: prevLeadDetails?.lease?.unit || getLeaseDurationUnit(region) || "",
    };
  }

  const currentTagCodes = values.selectedTagOptions || [];
  const existingTagCodes = prevLeadDetails?.tags?.map((tag) => tag.code) || [];
  if (
    JSON.stringify(currentTagCodes.sort()) !==
    JSON.stringify(existingTagCodes.sort())
  ) {
    payload.tags =
      values.selectedTagsDataWithColors &&
      values.selectedTagsDataWithColors.length > 0
        ? values.selectedTagsDataWithColors
        : [];
  }

  const utmSourceChanged =
    (values.utmSource || "") !==
    (prevLeadDetails?.tracking_info?.utm_source || "");
  const utmMediumChanged =
    (values.utmMedium || "") !==
    (prevLeadDetails?.tracking_info?.utm_medium || "");
  if (utmSourceChanged || utmMediumChanged) {
    payload.tracking_info = {
      utm_source: values.utmSource || null,
      utm_medium: values.utmMedium || null,
    };
  }

  const prevChoosenPropIds = (prevLeadDetails?.choosen_props || prevLeadDetails?.chosen_props || []).map((p) => p.property_id.toString()).sort();
  const currentChoosenPropIds = (values.choosenProps || []).map(String).sort();
  if (JSON.stringify(currentChoosenPropIds) !== JSON.stringify(prevChoosenPropIds)) {
    payload.choosen_props = values.choosenPropsPayload || [];
  }

  if (
    (values.currentStage || "") !==
    (prevLeadDetails?.current_stage || "")
  ) {
    payload.current_stage = values.currentStage || null;
  }

  const prevAcademicYear = (prevLeadDetails as unknown as Record<string, unknown>)?.academic_year as string || null;
  if ((values.moveInSession || null) !== (prevAcademicYear || null)) {
    payload.academic_year = values.moveInSession || null;
  }

  if (
    (values.howDidYouHearAboutUs || "") !==
    (prevLeadDetails?.how_did_you_hear_about_us || "")
  ) {
    payload.how_did_you_hear_about_us = values.howDidYouHearAboutUs || null;
  }

  const prevConsent = prevLeadDetails?.marketing_consent != null
    ? (prevLeadDetails.marketing_consent ? "Yes" : "No")
    : null;
  if ((values.marketingConsent || null) !== prevConsent) {
    payload.marketing_consent = values.marketingConsent
      ? values.marketingConsent === "Yes"
      : null;
  }

  const prevAgentIds: string[] = Array.isArray(prevLeadDetails?.agent)
    ? prevLeadDetails.agent.map((a: { id: string }) => a.id)
    : [];
  const currentAgentIds = values.assigned_agents || [];
  if (JSON.stringify([...currentAgentIds].sort()) !== JSON.stringify([...prevAgentIds].sort())) {
    payload.agent_id = currentAgentIds.length > 0 ? currentAgentIds : null;
  }

  return payload;
};

const parseRangeString = (range: string): { min: number; max: number } | null => {
  if (!range) return null;
  if (range.includes("+")) {
    const min = parseInt(range.replace("+", ""));
    return isNaN(min) ? null : { min, max: min };
  }
  const parts = range.split("-");
  if (parts.length === 2) {
    const min = parseInt(parts[0]);
    const max = parseInt(parts[1]);
    return isNaN(min) || isNaN(max) ? null : { min, max };
  }
  return null;
};

const rangeObjToString = (obj: unknown): string => {
  if (!obj || typeof obj !== "object") return "";
  const { min, max } = obj as { min?: number; max?: number };
  if (min == null || max == null) return "";
  return `${min}-${max}`;
};

export const formatVfEditLeadPayload = (
  values: VfLeadFormValues,
  prevLead: Record<string, unknown>
) => {
  const payload: Record<string, unknown> = {};

  if ((values.name || "") !== ((prevLead.name as string) || "")) {
    payload.name = values.name || null;
  }
  if ((values.email || "") !== ((prevLead.email as string) || "")) {
    payload.email = values.email || null;
  }

  const currPhoneEmpty = isEmptyPhoneNumber(values.phoneNumber);
  const prevPhoneEmpty = isEmptyPhoneNumber(prevLead.phone as string | null);
  const phoneChanged =
    currPhoneEmpty !== prevPhoneEmpty ||
    (!currPhoneEmpty &&
      !prevPhoneEmpty &&
      (values.phoneNumber?.trim() !== (prevLead.phone as string)?.trim() ||
        values.phoneCountry?.dialCode !== (prevLead.phone_country_code as string)));
  if (phoneChanged) {
    if (currPhoneEmpty) {
      payload.phone = null;
      payload.phone_country_code = null;
    } else if (values.phoneNumber && values.phoneCountry?.dialCode) {
      payload.phone = values.phoneNumber;
      payload.phone_country_code = values.phoneCountry.dialCode;
    }
  }

  if ((values.company_name || "") !== ((prevLead.company_name as string) || "")) {
    payload.company_name = values.company_name || null;
  }
  if ((values.industry || "") !== ((prevLead.industry as string) || "")) {
    payload.industry = values.industry || null;
  }
  if ((values.job_title || "") !== ((prevLead.job_title as string) || "")) {
    payload.job_title = values.job_title || null;
  }
  if ((values.how_can_verbaflo_help || "") !== ((prevLead.comment as string) || "")) {
    payload.comment = values.how_can_verbaflo_help || null;
  }

  const currBeds = values.number_of_beds_managed;
  const prevBeds = rangeObjToString(prevLead.number_of_beds);
  if (currBeds !== prevBeds) {
    payload.number_of_beds = parseRangeString(currBeds);
  }

  const currProps = values.number_of_properties;
  const prevProps = rangeObjToString(prevLead.number_of_properties);
  if (currProps !== prevProps) {
    payload.number_of_properties = parseRangeString(currProps);
  }

  const prevDemographics = (prevLead.demographics as Record<string, unknown>) || {};
  const prevCountryName = (prevDemographics.destination_country as string) || "";
  const prevCountryId = prevDemographics.destination_country_id?.toString() || "";
  const prevCityName = (prevDemographics.destination_city as string) || "";
  const prevCityId = prevDemographics.destination_city_id?.toString() || "";

  const countryChanged = (values.country_name || "") !== prevCountryName ||
    (values.country || "") !== prevCountryId;
  const cityChanged = (values.city_names?.[0] || "") !== prevCityName ||
    (values.city?.[0] || "") !== prevCityId;

  if (countryChanged || cityChanged) {
    payload.demographics = {
      ...((payload.demographics as Record<string, unknown>) || {}),
      ...(countryChanged && {
        destination_country: values.country_name || null,
        destination_country_id: values.country ? parseInt(values.country) : null,
      }),
      ...(cityChanged && {
        destination_city: values.city_names?.[0] || null,
        destination_city_id: values.city?.[0] ? parseInt(values.city[0]) : null,
      }),
    };
  }

  if ((values.office_location || "") !== ((prevLead.office_location as string) || "")) {
    payload.office_location = values.office_location || null;
  }
  if ((values.crm_currently_used || "") !== ((prevLead.external_crm_name as string) || "")) {
    payload.external_crm_name = values.crm_currently_used || null;
  }
  if ((values.pms_currently_used || "") !== ((prevLead.external_pms_name as string) || "")) {
    payload.external_pms_name = values.pms_currently_used || null;
  }
  if ((values.voip || "") !== ((prevLead.external_voip_name as string) || "")) {
    payload.external_voip_name = values.voip || null;
  }

  const demoKeys = ["demo_1_date", "demo_2_date", "demo_3_date", "demo_4_date"] as const;
  for (const key of demoKeys) {
    const currVal = values[key] || null;
    const prevVal = prevLead[key] ? String(prevLead[key]) : null;
    if (currVal !== prevVal) {
      payload[key] = currVal;
    }
  }
  const timezoneChanged = (values.timezone || "") !== ((prevDemographics.timezone as string) || "");
  if (timezoneChanged) {
    payload.demographics = {
      ...((payload.demographics as Record<string, unknown>) || {}),
      timezone: values.timezone || null,
    };
  }

  if ((values.priority || "") !== ((prevLead.verbaflo_request_priority as string) || "")) {
    payload.verbaflo_request_priority = values.priority || null;
  }
  if ((values.contact_person_name || "") !== ((prevLead.contact_person_name as string) || "")) {
    payload.contact_person_name = values.contact_person_name || null;
  }
  if ((values.contact_person_email || "") !== ((prevLead.contact_person_email as string) || "")) {
    payload.contact_person_email = values.contact_person_email || null;
  }
  if ((values.contact_person_designation || "") !== ((prevLead.contact_person_designation as string) || "")) {
    payload.contact_person_designation = values.contact_person_designation || null;
  }
  if ((values.website || "") !== ((prevLead.website_url as string) || "")) {
    payload.website_url = values.website || null;
  }
  if ((values.contract_link || "") !== ((prevLead.contract_link as string) || "")) {
    payload.contract_link = values.contract_link || null;
  }
  if ((values.fathom_link || "") !== ((prevLead.fathom_link as string) || "")) {
    payload.fathom_link = values.fathom_link || null;
  }
  if ((values.lead_source || "") !== ((prevLead.lead_source as string) || "")) {
    payload.lead_source = values.lead_source || null;
  }
  const prevAgentIds: string[] = Array.isArray(prevLead.agent)
    ? (prevLead.agent as { id: string }[]).map((a) => a.id)
    : [];
  if (JSON.stringify([...values.agent_id].sort()) !== JSON.stringify([...prevAgentIds].sort())) {
    payload.agent_id = values.agent_id.length > 0 ? values.agent_id : null;
  }

  return payload;
};

export const getGlobalCrmFilters = (leadsFilters: CrmLeadsFilters) => {
  const duration = leadsFilters?.duration;
  const sourceCountry = leadsFilters?.sourceCountry;
  const destinationCountry = leadsFilters?.destinationCountry;
  const destinationCity = leadsFilters?.destinationCity;
  const university = leadsFilters?.university;
  const assignedAgents = leadsFilters?.assignedAgents;
  const tags = leadsFilters?.tags;
  const roomType = leadsFilters?.roomType;
  const academicYear = leadsFilters?.academicYear;
  const leadStage = leadsFilters?.leadStage;
  const dealStage = leadsFilters?.dealStage;
  const dealPropertyName = leadsFilters?.dealPropertyName;
  const dealRoomType = leadsFilters?.dealRoomType;
  const dealAcademicYear = leadsFilters?.dealAcademicYear;
  const dealAvailabilityTitle = leadsFilters?.dealAvailabilityTitle;
  const dealCity = leadsFilters?.dealCity;
  const dealAssignee = leadsFilters?.dealAssignee;
  const dealRoomNumber = leadsFilters?.dealRoomNumber;
  const leadType = leadsFilters?.leadType;
  const viewing = leadsFilters?.viewing;
  const repeatLead = leadsFilters?.repeatLead;
  const communicationType = leadsFilters?.communicationType;
  const property = leadsFilters?.property;
  const utmSources = leadsFilters?.utmSources;
  const utmMedium = leadsFilters?.utmMedium;
  const attributionChannel = leadsFilters?.attributionChannel;
  const standardMedium = leadsFilters?.standardMedium;
  const standardSource = leadsFilters?.standardSource;
  const deviceType = leadsFilters?.deviceType;
  const bedCount = leadsFilters?.bedCount;
  const propertyCount = leadsFilters?.propertyCount;
  // const activeFollowUps = leadsFilters?.activeFollowUps;
  const leadsNotUpdated30Days = leadsFilters?.leadsNotUpdated30Days;
  const lastActivity = leadsFilters?.lastActivity;

  const parseRange = (range: string): { min: number; max: number } | null => {
    if (!range) return null;
    if (range.includes("+")) {
      const min = parseInt(range.replace("+", ""));
      return isNaN(min) ? null : { min, max: min };
    }
    const parts = range.split("-");
    if (parts.length === 2) {
      const min = parseInt(parts[0]);
      const max = parseInt(parts[1]);
      return isNaN(min) || isNaN(max) ? null : { min, max };
    }
    return null;
  };

  const lastActivityRange = lastActivity?.value
    ? getDateRangeFromPreset(lastActivity.value as string)
    : null;

  const globalCrmFilters = {
    start_date: duration?.startDate,
    end_date: duration?.endDate,
    ...(sourceCountry?.value &&
      sourceCountry?.value?.length > 0 && {
        source_country: sourceCountry?.value,
      }),
    ...(destinationCountry?.value &&
      destinationCountry?.value?.length > 0 && {
        destination_country: destinationCountry?.value,
      }),
    ...(destinationCity?.value &&
      destinationCity?.value?.length > 0 && {
        destination_city: destinationCity?.value,
      }),
    ...(university?.value && { university: university?.value }),
    ...(assignedAgents?.value &&
      assignedAgents?.value?.length > 0 && {
        assigned_agents: assignedAgents?.value,
      }),
    ...(tags?.value && tags?.value?.length > 0 && { tags: tags?.value }),
    ...(roomType?.value &&
      roomType?.value?.length > 0 && {
        room_types: roomType?.value,
      }),
    ...(academicYear?.value &&
      academicYear?.value?.length > 0 && {
        academic_year: academicYear?.value,
      }),
    ...(leadStage?.value &&
      leadStage?.value?.length > 0 && {
        current_stage: leadStage?.value,
      }),
    ...(dealStage?.value &&
      dealStage?.value?.length > 0 && {
        deal_stage: dealStage?.value,
      }),
    ...(dealPropertyName?.value &&
      dealPropertyName?.value?.length > 0 && {
        deal_property_name: dealPropertyName?.value,
      }),
    ...(dealRoomType?.value &&
      dealRoomType?.value?.length > 0 && {
        deal_room_type: dealRoomType?.value,
      }),
    ...(dealAcademicYear?.value &&
      dealAcademicYear?.value?.length > 0 && {
        deal_academic_year: dealAcademicYear?.value,
      }),
    ...(dealAvailabilityTitle?.value &&
      dealAvailabilityTitle?.value?.length > 0 && {
        deal_availability_title: dealAvailabilityTitle?.value,
      }),
    ...(dealCity?.value &&
      dealCity?.value?.length > 0 && {
        deal_city: dealCity?.value,
      }),
    ...(dealAssignee?.value &&
      dealAssignee?.value?.length > 0 && {
        deal_assignee: dealAssignee?.value,
      }),
    ...(dealRoomNumber?.value && {
      deal_room_number: dealRoomNumber?.value,
    }),
    ...(leadType?.value && { tenant_type: [leadType?.value] }),
    ...(viewing && { has_viewing: viewing }),
    ...(repeatLead && { exclude_repeated_leads: repeatLead }),
    ...(communicationType?.value && {
      lead_direction: communicationType?.value,
    }),
    ...(property?.value &&
      property?.value?.length > 0 &&
      ((property.value as string[]).includes("no_property_assigned")
        ? { properties: [], has_no_property: true }
        : { properties: property?.value, has_no_property: false })),
    ...(utmSources?.value &&
      utmSources?.value?.length > 0 && {
        utm_source: utmSources?.value,
      }),
    ...(utmMedium?.value &&
      utmMedium?.value?.length > 0 && {
        utm_medium: utmMedium?.value,
      }),
    ...(attributionChannel?.value &&
      attributionChannel?.value?.length > 0 && {
        attribution_channel: attributionChannel?.value,
      }),
    ...(standardMedium?.value &&
      standardMedium?.value?.length > 0 && {
        standard_medium: standardMedium?.value,
      }),
    ...(standardSource?.value &&
      standardSource?.value?.length > 0 && {
        standard_source: standardSource?.value,
      }),
    ...(deviceType?.value &&
      deviceType?.value?.length > 0 && {
        device_type: deviceType?.value,
      }),
    ...(bedCount?.value && {
      number_of_beds: parseRange(bedCount.value as string),
    }),
    ...(propertyCount?.value && {
      number_of_properties: parseRange(propertyCount.value as string),
    }),
    ...(leadsNotUpdated30Days && {
      not_updated_in_30_days: leadsNotUpdated30Days,
    }),
    ...(lastActivityRange && {
      updated_after: lastActivityRange.startDate,
    }),
  };
  return cleanObject(globalCrmFilters);
};

export const cleanObject = (obj: Record<string, unknown>) => {
  return Object.fromEntries(
    Object.entries(obj).filter(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ([_, value]) => value !== null && value !== undefined && value !== ""
    )
  );
};

type LeadDetailsSidebarItem =
  (typeof CRM_LEAD_DETAILS_RIGHT_PANEL_SIDEBAR_ITEMS)[number];

export const getLeadDetailsSidebarItems = (
  configTabs?: Array<{ key: string }>
): LeadDetailsSidebarItem[] => {
  if (!configTabs || configTabs.length === 0) {
    return CRM_LEAD_DETAILS_RIGHT_PANEL_SIDEBAR_ITEMS;
  }
  return configTabs
    .map((tab) =>
      CRM_LEAD_DETAILS_RIGHT_PANEL_SIDEBAR_ITEMS.find(
        (item) => item.configKey === tab.key
      )
    )
    .filter((item): item is LeadDetailsSidebarItem => Boolean(item));
};
